/**
 * Shows whether this student's result is hidden in the app because of fee due
 * (setting "Hide Result if Fee Due") and lets staff allow it for this student (principal permission).
 * Renders nothing when the setting is OFF.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import schoolApi from "../../api/schoolApi";

const inr = (v) => Math.round(Number(v) || 0).toLocaleString("en-IN");

const ResultHoldControl = ({ admissionId, refreshKey }) => {
  const [st, setSt] = useState(null);
  const [show, setShow] = useState(false);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!admissionId) return;
    try {
      const r = await schoolApi.getResultBlock(admissionId);
      setSt(r?.success ? r.result : null);
    } catch {
      setSt(null);
    }
  }, [admissionId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!st || !st.enabled) return null;

  const save = async (exempt) => {
    if (exempt && !remark.trim()) return toast.warn("Please write who allowed it / reason");
    setSaving(true);
    try {
      const r = await schoolApi.saveResultBlock(admissionId, { exempt, remark: remark.trim() });
      if (r?.success) {
        setSt(r.result);
        setShow(false);
        toast.success(exempt ? "Result allowed in app for this student" : "Result hold is back to normal");
      } else toast.error(r?.message || "Could not save");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  let badge;
  if (st.exempt) badge = <Badge bg="info">Result visible in app – allowed{st.remark ? `: ${st.remark}` : ""}</Badge>;
  else if (st.blocked) badge = <Badge bg="danger">Result hidden in app – fee due ₹{inr(st.due_amount)}</Badge>;
  else badge = <Badge bg="success">Result visible in app</Badge>;

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
      {badge}
      {st.exempt ? (
        <Button size="sm" variant="outline-secondary" disabled={saving} onClick={() => save(false)}>
          Remove permission
        </Button>
      ) : (
        st.blocked && (
          <Button size="sm" variant="outline-primary" onClick={() => { setRemark(""); setShow(true); }}>
            Allow result anyway
          </Button>
        )
      )}
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 17 }}>Allow result in app</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Fee due till today: <strong>₹{inr(st.due_amount)}</strong>. The student / parent will be able to see the
            result in the app even with this due.
          </p>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={255}
            placeholder="Who allowed it / reason (e.g. Principal – will pay on 10th)"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShow(false)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={() => save(true)}>
            {saving ? "Saving..." : "Allow"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResultHoldControl;
